/**
 * Хуки данных модуля заявок гостя (Tanstack Query поверх @hsc/api-client).
 *
 * Бэкенд: GET /v1/me/requests, GET /v1/requests/{id} (+ комментарии),
 * POST /v1/requests/{id}/cancel, POST /v1/requests/{id}/comments,
 * GET /v1/requests/{id}/sla-status.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addRequestComment,
  cancelRequest,
  getRequest,
  getRequestSLAStatus,
  listMyRequests,
} from '@hsc/api-client';
import type {
  RequestComment,
  RequestCommentInput,
  RequestListResponse,
  RequestSLAStatus,
  RequestView,
  RequestWithComments,
} from '@hsc/types';

/** GET /v1/me/requests — список заявок гостя. */
export function useMyRequests() {
  return useQuery<RequestListResponse>({
    queryKey: ['requests', 'mine'],
    queryFn: () => listMyRequests(),
  });
}

/** GET /v1/requests/{id} — карточка заявки с комментариями. */
export function useRequestDetail(id: string) {
  return useQuery<RequestWithComments>({
    queryKey: ['requests', 'detail', id],
    queryFn: () => getRequest(id),
    enabled: id !== '',
  });
}

/** GET /v1/requests/{id}/sla-status — статус SLA (трекинг). */
export function useRequestSLA(id: string, enabled: boolean) {
  return useQuery<RequestSLAStatus>({
    queryKey: ['requests', 'sla', id],
    queryFn: () => getRequestSLAStatus(id),
    enabled: enabled && id !== '',
  });
}

/** POST /v1/requests/{id}/cancel — отмена заявки гостем. */
export function useCancelRequest(id: string) {
  const qc = useQueryClient();
  return useMutation<RequestView, Error, string | undefined>({
    mutationFn: (reason) => cancelRequest(id, reason ? { reason } : {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['requests', 'detail', id] });
      void qc.invalidateQueries({ queryKey: ['requests', 'mine'] });
      void qc.invalidateQueries({ queryKey: ['requests', 'sla', id] });
    },
  });
}

/** POST /v1/requests/{id}/comments — отправить сообщение в чат заявки. */
export function useAddComment(id: string) {
  const qc = useQueryClient();
  return useMutation<RequestComment, Error, RequestCommentInput>({
    mutationFn: (input) => addRequestComment(id, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['requests', 'detail', id] });
    },
  });
}
